export interface postJQLSearchResponse {
  issues: JsmIssueBase[];
  total: number;
}

export interface CommentBody {
  type: string;
  version: number;
  content: {
    type: string;
    content: {
      type: string;
      text: string;
      attrs?: {
        type: string;
        id: string;
        collection: string;
      };
    }[];
  }[];
}

export interface PaginationModel {
  page: number;
  pageSize: number;
}

export interface Comment {
  id: string;
  self: string;
  author: Author;
  body: CommentBody;
  created: string; // Date string
  updated: string; // Date string
  renderedBody: string;
  jsdPublic: boolean;
}

export interface Attachment {
  id: string;
  filename: string;
  content: string;
  thumbnail?: string;
  mimeType: string;
  size: number; // Size in bytes
  created: string; // Date string
  author: Author;
}

export interface Author {
  self?: string;
  accountId?: string;
  emailAddress?: string;
  avatarUrls: {
    small: string;
    medium?: string;
    large?: string;
  };
  displayName: string;
  active: boolean;
}

export interface Comment {
  id: string;
  self: string;
  author: Author;
  body: CommentBody;
  updateAuthor: Author;
  created: string; // Date string
  updated: string; // Date string
  renderedBody: string;
  jsdPublic: boolean;
}

export interface Version {
  archived: boolean;
  description: string;
  id: string;
  name: string;
  releaseDate: string; //human readable y-m-d
  released: boolean;
  self: string;
}

export interface IssueLink {
  outwardIssue?: JsmIssueBase;
  inwardIssue?: JsmIssueBase;
  id: string;
  self: string;
  type: {
    id: string; //ie 10002
    inward: string; //ie is duplicated by
    name: string; //ie Duplicate
    outward: string; //ie duplicates
    self: string;
  };
}

export interface JsmIssueBase {
  id: string;
  key: string;
  renderedFields: {
    description: string;
    comment: {
      self: string;
      maxResults: number;
      total: number;
      startAt: number;
      comments: Comment[];
    };
    attachment: Attachment[];
  };
  fields: {
    summary: string; // not an id, just the plaintext summary
    assignee?: {
      accountId: string;
      accountType: string;
      active: boolean;
      displayName: string;
      self: string;
      timeZone: string;
    };
    attachment: Attachment[];
    created: string; // string for created-at time, ISO 8601 format
    updated: string; // also ISO 8601 format
    description: {
      content: [
        {
          type: string;
          content: [
            {
              type: string;
              text: string;
            },
          ];
        },
      ];
    };
    reporter?: {
      accountId?: string; // probably don't need to surface this
      accountType: string; // Atlassian vs customer, etc.
      emailAddress?: string;
      active: boolean;
      avatarUrls: {
        '16x16': string;
        '24x24': string;
        '32x32': string;
        '48x48': string;
      };
      displayName: string;
      timeZone: string;
      self?: string;
    };
    comment: {
      comments: Comment[];
    };
    versions: Version[];
    fixVersions: Version[];
    labels: [];
    resolution: {
      description: string;
      id: string;
      name: string;
      self: string;
    };
    resolutiondate: string;

    issuetype: {
      avatarId: number;
      descrption: string;
      hierarchyLevel: number;
      iconUrl: string;
      id: string;
      name: string;
      self: string;
      subtask: boolean;
    };
    status: {
      description: string;
      iconUrl: string;
      id: string;
      name: string; // e.g., "Open", "Resolved", etc.
      self: string;
      statusCategory: {
        colorName: string;
        id: number;
        key: string;
        name: string;
        self: string;
      };
    };

    //
    // Custom fields and their applicable project keys:
    //
    // CHK => REALMS, MCD, BDS, MCLG, MCPE, MC, WEB, MCL
    customfield_10047: string;

    // Confirmation status => REALMS, MCD, BDS, MCLG, MCPE, MC, WEB, MCL
    customfield_10054: {
      id: string;
      self: string;
      value: string; // e.g., "Confirmed", "Unconfirmed"
    };

    // Category => REALMS, BDS, MC
    customfield_10055: [
      {
        self: string;
        value: string;
        id: string;
      },
    ];

    // Area => REALMS, BDS, MC
    customfield_10051: {
      self: string;
      value: string;
      id: string;
    };

    // Platform => MCLG, MCPE, MCD
    customfield_10063: {
      self: string;
      value: string;
      id: string;
    };

    // Dungeons Platform => MCLG, MCD, MCPE
    customfield_10062: {
      self: string;
      value: string;
      id: string;
    };

    // ADO => REALMS, MCD, BDS, MCL, MCPE, MC, WEB, MCLG
    customfield_10050: string;

    // Game Mode => REALMS, MCD, BDS, MCL, MCPE, MC, WEB, MCLG
    customfield_10048: {
      self: string;
      value: string;
      id: string;
    };

    // Affected Platforms => REALMS, BDS, MC, WEB
    customfield_10053: {
      self: string;
      value: string;
      id: string;
    };

    // Realms Platform => MCL, MCPE, MCD, REALMS, MC, BDS, MCLG
    customfield_10056: {
      self: string;
      value: string;
      id: string;
    };

    // Legends Platform => MCLG, MCPE, MCD
    customfield_10060: {
      self: string;
      value: string;
      id: string;
    };

    // Operating System Version => MCLG, MCPE, MCD
    customfield_10061: {
      self: string;
      value: string;
      id: string;
    };

    // Moj prio => REALMS, BDS, MC
    customfield_10049: {
      self: string;
      value: string;
      id: string;
    };

    // Votes
    customfield_10070: string;
    watchers: {
      watchCount: number;
    };
    issuelinks: IssueLink[];
    self: string;
  };
}

export interface JsmIssueBaseOld {
  id: string;
  key: string;
  renderedFields: {
    description: string;
    comment: {
      self: string;
      maxResults: number;
      total: number;
      startAt: number;
      comments: Comment[];
    };
  };
  fields: {
    summary: string; // not an id, just the plaintext summary
    assignee?: {
      accountId: string;
      accountType: string;
      active: boolean;
      displayName: string;
      self: string;
      timeZone: string;
    };
    created: string; // string for created-at time, ISO 8601 format
    updated: string; // also ISO 8601 format
    description: {
      content: [
        {
          type: string;
          content: [
            {
              type: string;
              text: string;
            },
          ];
        },
      ];
    };
    reporter?: {
      accountId?: string; // probably don't need to surface this
      accountType: string; // Atlassian vs customer, etc.
      emailAddress?: string;
      active: boolean;
      avatarUrls: {
        '16x16': string;
        '24x24': string;
        '32x32': string;
        '48x48': string;
      };
      displayName: string;
      timeZone: string;
      self?: string;
    };
    comment: {
      comments: Comment[];
    };
    versions: Version[];
    fixVersions: Version[];
    labels: [];
    resolution: {
      description: string;
      id: string;
      name: string;
      self: string;
    };
    resolutiondate: string;

    issuetype: {
      avatarId: number;
      descrption: string;
      hierarchyLevel: number;
      iconUrl: string;
      id: string;
      name: string;
      self: string;
      subtask: boolean;
    };
    status: {
      description: string;
      iconUrl: string;
      id: string;
      name: string; // e.g., "Open", "Resolved", etc.
      self: string;
      statusCategory: {
        colorName: string;
        id: number;
        key: string;
        name: string;
        self: string;
      };
    };

    // SLA field (customfield_10050)
    customfield_10050: {
      completeCycles: unknown[];
      id: string;
      name: string;
      ongoingCycle: {
        breachTime: {
          epochMillis: number;
          friendly: string; // human readable string
          iso8601: string;
          jira: string; // date-time in Jira format
        };
        breached: boolean;
        elapsedTime: {
          millis: number;
          friendly: string; // e.g., "1d 2h"
        };
        goalDuration: {
          millis: number;
          friendly: string;
        };
        paused: boolean;
        remainingTime: {
          millis: number;
          friendly: string;
        };
        startTime: {
          epochMillis: number;
          friendly: string;
          iso8601: string;
          jira: string;
        };
        withCalendarHours: boolean;
      };
    };

    //
    // Custom fields and their applicable project keys:
    //
    // CHK => REALMS, MCD, BDS, MCLG, MCPE, MC, WEB, MCL
    customfield_10100: string;

    // Confirmation status => REALMS, MCD, BDS, MCLG, MCPE, MC, WEB, MCL
    customfield_10101: {
      id: string;
      self: string;
      value: string; // e.g., "Confirmed", "Unconfirmed"
    };

    // Category => REALMS, BDS, MC
    customfield_10102: [
      {
        self: string;
        value: string;
        id: string;
      },
    ];

    // Area => REALMS, BDS, MC
    customfield_10103: {
      self: string;
      value: string;
      id: string;
    };

    // Platform => MCLG, MCPE, MCD
    customfield_10105: {
      self: string;
      value: string;
      id: string;
    };

    // Dungeons Platform => MCLG, MCD, MCPE
    customfield_10106: {
      self: string;
      value: string;
      id: string;
    };

    // ADO => REALMS, MCD, BDS, MCL, MCPE, MC, WEB, MCLG
    customfield_10107: string;

    // Game Mode => REALMS, MCD, BDS, MCL, MCPE, MC, WEB, MCLG
    customfield_10108: {
      self: string;
      value: string;
      id: string;
    };

    // Affected Platforms => REALMS, BDS, MC, WEB
    customfield_10109: {
      self: string;
      value: string;
      id: string;
    };

    // Realms Platform => MCL, MCPE, MCD, REALMS, MC, BDS, MCLG
    customfield_10110: {
      self: string;
      value: string;
      id: string;
    };

    // Legends Platform => MCLG, MCPE, MCD
    customfield_10111: {
      self: string;
      value: string;
      id: string;
    };

    // Operating System Version => MCLG, MCPE, MCD
    customfield_10112: {
      self: string;
      value: string;
      id: string;
    };

    // Moj prio => REALMS, BDS, MC
    customfield_10116: {
      self: string;
      value: string;
      id: string;
    };

    // Operating System Version => MCLG, MCPE, MCD
    customfield_10309: string;

    // Secondary description (customfield_10310)
    customfield_10310: {
      content: [];
      version: number;
      type: string;
    };

    // Votes
    customfield_10070: string;
    watchers: {
      watchCount: number;
    };
    issuelinks: IssueLink[];
    self: string;
  };
}

export interface PaginationModel {
  page: number;
  pageSize: number;
}

export interface SearchIssuesResponse {
  expand: string;
  startAt: number;
  maxResults: number;
  total: number;
  issues: JsmIssueBase[];
  schema?: Record<string, unknown>;
}

export const portalOrder = ['2', '6', '9', '7', '4', '10'];

export interface PortalDetails {
  project: string;
  portalName: string;
  portalLogoImageUrl: string;
  portalIconUrl: string;
  customFields: string[];
}

export type PortalMap = {
  [key: string]: PortalDetails;
};

export const projectPortalMap: PortalMap = {
  '9': {
    project: 'REALMS',
    portalName: 'Minecraft Realms',
    portalLogoImageUrl: '/Minecraft_Realms_Logo.svg',
    portalIconUrl: '/realms_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10055',
      'customfield_10051',
      'customfield_10050',
      'customfield_10048',
      'customfield_10053',
      'customfield_10056',
      'customfield_10049',
    ],
  },
  '3': {
    project: 'MCD',
    portalName: 'Minecraft Dungeons',
    portalLogoImageUrl: '/Minecraft_Dungeons.svg',
    portalIconUrl: '/dungeons_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10063',
      'customfield_10062',
      'customfield_10050',
      'customfield_10048',
      'customfield_10060',
      'customfield_10061',
    ],
  },
  '4': {
    project: 'BDS',
    portalName: 'Bedrock Dedicated Server',
    portalLogoImageUrl: '',
    portalIconUrl: 'bds_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10055',
      'customfield_10051',
      'customfield_10050',
      'customfield_10048',
      'customfield_10053',
      'customfield_10056',
      'customfield_10049',
    ],
  },
  '7': {
    project: 'MCL',
    portalName: 'Minecraft Launcher',
    portalLogoImageUrl: '',
    portalIconUrl: '/mcl_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10063',
      'customfield_10062',
      'customfield_10050',
      'customfield_10048',
      'customfield_10056',
      'customfield_10060',
      'customfield_10061',
    ],
  },
  '6': {
    project: 'MCPE',
    portalName: 'Minecraft (Bedrock codebase)',
    portalLogoImageUrl: '/Minecraft_Bedrock.svg',
    portalIconUrl: '/mcpe_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10063',
      'customfield_10062',
      'customfield_10050',
      'customfield_10048',
      'customfield_10056',
      'customfield_10060',
      'customfield_10061',
    ],
  },
  '8': {
    project: 'MCLG',
    portalName: 'Minecraft Legends',
    portalLogoImageUrl: '/Minecraft_Legends_Logo.png',
    portalIconUrl: '/legends_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10063',
      'customfield_10062',
      'customfield_10050',
      'customfield_10048',
      'customfield_10056',
      'customfield_10060',
      'customfield_10061',
    ],
  },
  '2': {
    project: 'MC',
    portalName: 'Minecraft: Java Edition',
    portalLogoImageUrl: '/Java_Edition_logo_13.webp',
    portalIconUrl: '/mc_icon.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10055',
      'customfield_10051',
      'customfield_10050',
      'customfield_10048',
      'customfield_10053',
      'customfield_10056',
      'customfield_10049',
    ],
  },
  '10': {
    project: 'WEB',
    portalName: 'Mojang Web Services',
    portalLogoImageUrl: '',
    portalIconUrl: '/command_block_web_square.png',
    customFields: [
      'customfield_10047',
      'customfield_10054',
      'customfield_10055',
      'customfield_10051',
      'customfield_10050',
      'customfield_10048',
      'customfield_10053',
      'customfield_10056',
      'customfield_10049',
    ],
  },
};

export function getKeyByProject(
  projectName: string | undefined
): string | undefined {
  return Object.keys(projectPortalMap).find(
    (key) => projectPortalMap[key].project === projectName
  );
}

export function getProjectByIssueKey(issueKey: string): string {
  const mainKey = issueKey.split('-')[0];

  if (!mainKey) return '';
  const projectData = Object.values(projectPortalMap).find(
    (v) => v.project == mainKey
  );
  return projectData ? projectData.project : '';
}

export function safeDecode(value: string | null): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

export function buildParamJql({
  filter,
  label,
  fixVersion,
}: {
  filter: string;
  label: string;
  fixVersion: string;
}) {
  const parts: string[] = [];

  if (filter && filter !== 'null' && filter !== 'undefined') {
    parts.push(`filter = "${filter}"`);
  }
  if (label && label !== 'null' && label !== 'undefined') {
    parts.push(`labels = "${label}"`);
  }
  if (fixVersion && fixVersion !== 'null' && fixVersion !== 'undefined') {
    parts.push(`fixVersion = "${fixVersion}"`);
  }

  return parts.join(' AND ');
}
